import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BlockRuleCriteriaType,
  BlockRuleType,
  CreateBlockRuleRequestDto,
} from "@repo/api/block-rule";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { toast } from "sonner";

const criteriaTypes = BlockRuleCriteriaType.options;
const blockTypes = BlockRuleType.options;

export default function BlockRules() {
  const queryClient = useQueryClient();

  const [criteriaType, setCriteriaType] =
    useState<(typeof criteriaTypes)[number]>("COUNTRY");
  const [blockType, setBlockType] =
    useState<(typeof blockTypes)[number]>("INITIAL_CREDITS");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["adminBlockRules"],
    queryFn: () => AdminApi.listBlockRules(),
    retry: false,
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateBlockRuleRequestDto) =>
      AdminApi.createBlockRule(body),
    onSuccess: () => {
      toast.success("Block rule created");
      setValue("");
      setReason("");
      queryClient.invalidateQueries({ queryKey: ["adminBlockRules"] });
    },
    onError: (err) => {
      toast.error(`Failed to create block rule: ${err.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (externalId: string) => AdminApi.deleteBlockRule(externalId),
    onSuccess: () => {
      toast.success("Block rule deleted");
      queryClient.invalidateQueries({ queryKey: ["adminBlockRules"] });
    },
    onError: (err) => {
      toast.error(`Failed to delete block rule: ${err.message}`);
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;

    createMutation.mutate({
      criteriaType,
      blockType,
      value: value.trim(),
      reason: reason.trim() || null,
    });
  };

  const rules = data?.rules ?? [];

  return (
    <AdminLayout
      title="Block Rules"
      description="Block use cases (e.g. initial credits) by country or IP address"
      breadcrumb="Block Rules"
    >
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Add block rule</h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Block type
                </label>
                <select
                  value={blockType}
                  onChange={(e) =>
                    setBlockType(e.target.value as (typeof blockTypes)[number])
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {blockTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Criteria type
                </label>
                <select
                  value={criteriaType}
                  onChange={(e) =>
                    setCriteriaType(
                      e.target.value as (typeof criteriaTypes)[number],
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {criteriaTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={
                  criteriaType === "COUNTRY"
                    ? "Country code (e.g. RU)"
                    : "IP address (e.g. 1.2.3.4)"
                }
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Why this rule exists"
              />
            </div>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-6 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? "Adding..." : "Add block rule"}
            </button>
          </form>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 text-sm">
              {error instanceof Error ? error.message : "Failed to load rules"}
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Active block rules</h3>
            <p className="text-sm text-gray-500 mt-1">
              {rules.length} rule{rules.length !== 1 ? "s" : ""}
            </p>
          </div>

          {isLoading ? (
            <div className="px-6 py-8 text-center text-gray-500">
              Loading...
            </div>
          ) : rules.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No block rules.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Block type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criteria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rules.map((rule) => (
                    <tr key={rule.externalId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rule.blockType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rule.criteriaType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {rule.value}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {rule.reason || "(no reason)"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(rule.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete this block rule?\n\n${rule.blockType} · ${rule.criteriaType}: ${rule.value}`,
                              )
                            ) {
                              deleteMutation.mutate(rule.externalId);
                            }
                          }}
                          disabled={deleteMutation.isPending}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
